import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CreditCard, Check, Star, Loader2, ShoppingBag, CheckCircle } from 'lucide-react';
import { getBillingPackages, createCheckout, getPaymentStatus, getMyPurchases } from '../utils/api';
import { toast } from 'sonner';
import AgentLayout from '../components/AgentLayout';

const AgentBilling = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    if (!token) { navigate('/agent/login'); return; }
    loadData(token);
  }, [navigate]);

  const loadData = async (token) => {
    try {
      setIsLoading(true);
      const [pkgRes, purchRes] = await Promise.all([
        getBillingPackages(),
        getMyPurchases(token)
      ]);
      setPackages(pkgRes.packages || []);
      setPurchases(purchRes.transactions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckout = async (packageId) => {
    setCheckingOut(packageId);
    try {
      const token = localStorage.getItem('agentToken');
      const originUrl = window.location.origin;
      const result = await createCheckout(packageId, originUrl, token);
      if (result.free) {
        toast.success('Free training enrolled successfully!');
        loadData(token);
        setCheckingOut(null);
      } else if (result.url) {
        window.location.href = result.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start checkout');
      setCheckingOut(null);
    }
  };

  const isPurchased = (packageId) => {
    return purchases.some(p => p.packageId === packageId && p.paymentStatus === 'paid');
  };

  const tierColors = {
    basic: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-brand-blue', btn: 'bg-brand-blue hover:bg-blue-700' },
    advanced: { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'text-brand-orange', btn: 'bg-brand-orange hover:bg-orange-600' },
    premium: { bg: 'bg-purple-50', border: 'border-purple-300', accent: 'text-purple-700', btn: 'bg-purple-700 hover:bg-purple-800' },
  };

  if (isLoading) {
    return (
      <AgentLayout title="Training & Billing">
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div></div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Training & Billing">
      <div data-testid="billing-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invest in Your Growth</h2>
          <p className="text-gray-500">Select a training package to enhance your skills and boost your performance.</p>
        </div>

        {/* Packages */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="training-packages">
          {packages.map(pkg => {
            const colors = tierColors[pkg.id] || tierColors.basic;
            const purchased = isPurchased(pkg.id);
            return (
              <div key={pkg.id} className={`relative rounded-xl border-2 ${colors.border} ${colors.bg} p-6 flex flex-col`} data-testid={`package-${pkg.id}`}>
                {pkg.id === 'premium' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-700 text-white px-4 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                    <Star size={12} /> Best Value
                  </div>
                )}
                <h3 className={`text-xl font-bold ${colors.accent} mb-1`}>{pkg.name}</h3>
                <p className="text-sm text-gray-600 mb-4 flex-1">{pkg.description}</p>
                <div className="mb-4">
                  {pkg.amount === 0 ? (
                    <span className="text-3xl font-bold text-green-600">Free</span>
                  ) : (
                    <>
                      <span className="text-3xl font-bold text-gray-900">${pkg.amount}</span>
                      <span className="text-sm text-gray-400 ml-1">one-time</span>
                    </>
                  )}
                </div>
                <ul className="space-y-2 mb-6">
                  {pkg.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-700">
                      <Check size={14} className={colors.accent} /> {f}
                    </li>
                  ))}
                </ul>
                {purchased ? (
                  <div className="flex items-center justify-center gap-2 py-3 rounded-lg bg-green-100 text-green-700 font-semibold text-sm" data-testid={`purchased-${pkg.id}`}>
                    <CheckCircle size={16} /> Purchased
                  </div>
                ) : (
                  <button
                    onClick={() => handleCheckout(pkg.id)}
                    disabled={checkingOut === pkg.id}
                    className={`w-full py-3 rounded-lg ${colors.btn} text-white font-semibold text-sm transition flex items-center justify-center gap-2 disabled:opacity-50`}
                    data-testid={`checkout-${pkg.id}`}
                  >
                    {checkingOut === pkg.id ? (
                      <><Loader2 size={16} className="animate-spin" /> Processing...</>
                    ) : pkg.amount === 0 ? (
                      <>Enroll Free</>
                    ) : (
                      <><CreditCard size={16} /> Purchase</>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Purchase History */}
        {purchases.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="purchase-history">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
              <ShoppingBag size={20} className="text-brand-blue" /> Purchase History
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Package</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {purchases.map(tx => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{tx.packageName}</td>
                      <td className="px-4 py-3">${tx.amount?.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          tx.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                          tx.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>{tx.paymentStatus}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{new Date(tx.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AgentBilling;
